import type { $Enums } from '@prisma/client';
import { definePolicy, type Policy } from '../kernel';
import { allow, deny } from '../types';

/**
 * EDI 860 CHANGE REQUESTS — a customer's requested change to an EDI Sales Order, decided by the
 * seller (Sales Order family). The ORDER's window (`handle change requests`, PENDING | APPROVED)
 * lives on `salesOrderPolicy`; this policy is the REQUEST's own lifecycle.
 *
 *   PENDING ──approve──▶ APPROVED      a request is decided once: only a PENDING request can be
 *          └─reject───▶ REJECTED       approved or rejected (the order page offers exactly these)
 *   any ──reopen──▶ PENDING            no rule on either side today, so none is invented here
 */
export type EdiPoChangeRequestFacts = { status: $Enums.EdiActionStatus };

export const EDI_PO_CHANGE_REQUEST_OPS = ['approve', 'reject', 'reopen'] as const;
export type EdiPoChangeRequestOp = (typeof EDI_PO_CHANGE_REQUEST_OPS)[number];

export const ediPoChangeRequestPolicy: Policy<EdiPoChangeRequestFacts, never, EdiPoChangeRequestOp> = definePolicy<
  EdiPoChangeRequestFacts,
  never,
  EdiPoChangeRequestOp
>({
  kind: 'EDI change request',
  fields: [],
  ops: EDI_PO_CHANGE_REQUEST_OPS,
  requires: {},
  entity: (facts, action) => {
    if (action.type !== 'perform' || action.op === 'reopen' || facts.status === 'PENDING') return allow();

    return deny(
      'EDI_PO_CHANGE_REQUEST_ALREADY_DECIDED',
      `This change request was already ${facts.status === 'APPROVED' ? 'approved' : 'rejected'}.`
    );
  },
});
